import { api } from '@/api/client';
import { STALL_MS } from '@/lib/audioPlayerState';

/**
 * Произношение слов: предзагрузка и воспроизведение без обрыва.
 *
 * ⚠️ ЗВУК ОБРЫВАЛСЯ НА СЕРЕДИНЕ. В уроке слово проигрывалось так:
 * `const a = new Audio(url); a.play()`. После выхода из функции на объект никто
 * не ссылался, и сборщик мусора имел полное право убрать его прямо во время
 * проигрывания — «reliable» звучало как «relia». В движке айфона это случается
 * особенно охотно. Держим ОДИН элемент на всё приложение и переиспользуем его.
 *
 * ⚠️ И ВТОРАЯ ПРИЧИНА ТОЙ ЖЕ БЕДЫ: раньше ссылка запрашивалась у сервера в
 * момент нажатия. Пока шёл запрос, разрешение на воспроизведение, выданное
 * браузером под нажатие пальцем, успевало истечь. Поэтому ссылки берутся
 * ЗАРАНЕЕ — пачкой, при открытии страницы, — а нажатие только запускает готовое.
 */

/**
 * Звук не удалось получить — сообщить экрану.
 *
 * ⚠️ РАНЬШЕ ЗДЕСЬ БЫЛА ТИШИНА. Когда у нас кончились деньги у поставщика
 * синтеза, кнопка нажималась, звука не было, и человек думал, что сломался
 * телефон. Ошибка гасилась `catch {}` и никуда не писалась — мы узнали об этом
 * только от заказчика (задача 122). Теперь причина уходит и на экран, и в
 * журнал браузера.
 */
type Problem = 'pronounce' | 'playback';
const problemListeners = new Set<(p: Problem) => void>();

export function onAudioProblem(l: (p: Problem) => void): () => void {
  problemListeners.add(l);
  return () => problemListeners.delete(l);
}

function reportProblem(p: Problem, cause: unknown): void {
  console.warn('[audio] звук недоступен:', p, cause);
  problemListeners.forEach((l) => l(p));
}

/**
 * Состояние озвучки — чтобы кнопка могла показать, что идёт загрузка.
 *
 * ⚠️ ЗАЧЕМ ЭТО, А НЕ «СКАЧАТЬ, ПОТОМ ИГРАТЬ». Соблазн велик: скачал файл
 * целиком — знаешь наверняка, что готово. Так делает `useWordAudio` для слова
 * дня, и там это уместно: карточка одна и висит на экране заранее.
 *
 * Здесь так нельзя. Выше в этом файле записано, чем это кончается: пока идёт
 * ожидание сети, разрешение браузера, выданное под нажатие пальцем, истекает —
 * и звук не начинается вовсе. Поэтому `play()` вызывается сразу, как и раньше,
 * а состояние берётся из событий элемента постфактум.
 *
 * Ученику с плохим интернетом важно видеть «идёт загрузка», а не гадать,
 * зависла ли платформа.
 */
export type AudioState = 'idle' | 'loading' | 'playing' | 'error';

let currentWord = '';
let currentState: AudioState = 'idle';
const stateListeners = new Set<() => void>();

/**
 * Сторож застрявшей загрузки.
 *
 * ⚠️ ЗАЧЕМ ОН ЗДЕСЬ ПОЯВИЛСЯ (12.09.2026). У общего плеера такой сторож стоит
 * с 10.09: элемент умеет застрять в загрузке, не прислав ни одного события —
 * ни `playing`, ни `error`. У кнопки озвучки его не было, и в этом случае она
 * крутилась ВЕЧНО: ученик уходил со страницы, так и не узнав, что произошло.
 * Порог тот же, что у плеера, — одна беда, одно число.
 */
let stallTimer: ReturnType<typeof setTimeout> | null = null;

function clearStall(): void {
  if (stallTimer === null) return;
  clearTimeout(stallTimer);
  stallTimer = null;
}

function armStall(word: string): void {
  stallTimer = setTimeout(() => {
    stallTimer = null;
    // Слово сменилось или звук всё-таки пошёл — сторожу здесь делать нечего.
    if (currentWord !== word || currentState !== 'loading') return;
    // ⚠️ Скрытую вкладку не судим: Chrome в ней загрузку звука не начинает
    // вовсе, и «не загрузилось» было бы ложью. Ждём, пока вкладку покажут.
    if (typeof document !== 'undefined' && document.hidden) {
      armStall(word);
      return;
    }
    setState(word, 'error');
    reportProblem('playback', `звука нет ${STALL_MS} мс — загрузка застряла`);
  }, STALL_MS);
}

function setState(word: string, state: AudioState): void {
  if (currentWord === word && currentState === state) return;
  currentWord = word;
  currentState = state;
  clearStall();
  if (state === 'loading') armStall(word);
  stateListeners.forEach((l) => l());
}

/** Подписка для useSyncExternalStore: дёргается при любой смене состояния. */
export function subscribeAudioState(l: () => void): () => void {
  stateListeners.add(l);
  return () => {
    stateListeners.delete(l);
  };
}

/**
 * Состояние КОНКРЕТНОГО слова. Элемент один на всё приложение, поэтому все
 * слова, кроме текущего, покоятся: нажал другое — предыдущее замолчало.
 */
export function audioStateOf(word: string): AudioState {
  return currentWord === word ? currentState : 'idle';
}

let element: HTMLAudioElement | null = null;

function player(): HTMLAudioElement {
  if (!element) {
    element = new Audio();
    element.preload = 'auto';
    // Событий много, а состояний четыре. `loadstart` и `waiting` значат «ждём
    // байты», `playing` — «пошло», `ended`/`pause` — «кончилось».
    const on = (name: string, state: AudioState) =>
      element?.addEventListener(name, () => setState(currentWord, state));
    on('loadstart', 'loading');
    on('waiting', 'loading');
    on('playing', 'playing');
    on('ended', 'idle');
    on('pause', 'idle');
    on('error', 'error');
  }
  return element;
}

/** Ссылки, уже полученные в этой сессии: word (как на экране) → адрес звука. */
const known = new Map<string, string>();

/** Запомнить готовые ссылки (например, `audio_url` из записи словаря). */
export function rememberAudio(pairs: Record<string, string | undefined>) {
  for (const [word, url] of Object.entries(pairs)) {
    if (url) known.set(word, url);
  }
}

/**
 * Заранее получить произношения для списка слов — ОДНИМ запросом.
 * Уже известные не спрашиваются повторно.
 */
export async function preloadWords(words: string[]): Promise<void> {
  const missing = [...new Set(words.filter((w) => w && !known.has(w)))];
  if (missing.length === 0) return;
  try {
    const { data } = await api.post<{ audio?: Record<string, string> }>(
      '/vocab/student/pronounce/batch',
      { words: missing },
    );
    const audio = (data as unknown as { audio?: Record<string, string> })?.audio ?? {};
    rememberAudio(audio);
  } catch (e) {
    // Экрану не сообщаем: предзагрузка — дело фоновое, нажатие спросит сервер
    // само и вот тогда скажет человеку. Но в журнал причину пишем.
    console.warn('[audio] предзагрузка произношений не удалась:', e);
  }
}

/** Есть ли уже готовая ссылка — чтобы экран мог не показывать кнопку впустую. */
export function hasAudio(word: string): boolean {
  return known.has(word);
}

/**
 * Проиграть слово.
 *
 * Если ссылка уже известна — звук стартует СРАЗУ, внутри обработчика нажатия.
 * Если нет (предзагрузка не успела) — спросим сервер и проиграем следом.
 *
 * ⚠️ Голоса браузера здесь нет намеренно: у нас всё звучит одним голосом, и
 * запасной системный синтез — ровно то, от чего уходили.
 */
/**
 * Запустить ссылку на общем элементе и не соврать про исход.
 *
 * ⚠️ ОТКАЗ ПРИ СМЕНЕ SRC — НЕ ОШИБКА. Нажали второе слово, пока играло первое:
 * браузер отклоняет прежний `play()` с AbortError. Показывать на кнопке
 * поломку в этот момент — врать: всё работает ровно как задумано.
 */
function start(key: string, url: string, a: HTMLAudioElement): void {
  a.src = url;
  void a.play().catch((e: unknown) => {
    if ((e as DOMException)?.name === 'AbortError') return;
    if (currentWord !== key) return;
    setState(key, 'error');
    reportProblem('playback', e);
  });
}

/**
 * Проиграть готовую ссылку — карточки, «услышал и напечатал».
 *
 * Тот же общий элемент, что и у слов: два звука одновременно ученику не нужны,
 * а один элемент — единственная защита от обрыва сборщиком мусора.
 */
export function playSrc(url: string): void {
  setState(url, 'loading');
  start(url, url, player());
}

export async function playWord(word: string): Promise<void> {
  const a = player();
  const cached = known.get(word);
  // Слово становится текущим ДО обращения к сети: иначе первые события
  // элемента пришлись бы на предыдущее слово и кнопка мигнула бы не та.
  setState(word, 'loading');
  if (cached) {
    start(word, cached, a);
    return;
  }
  try {
    const { data } = await api.get<{ audio_url?: string }>('/vocab/student/pronounce', {
      params: { word },
    });
    const url = (data as unknown as { audio_url?: string })?.audio_url;
    if (!url) {
      setState(word, 'error');
      reportProblem('pronounce', 'сервер не вернул ссылку');
      return;
    }
    known.set(word, url);
    start(word, url, a);
  } catch (e) {
    // Ложный системный голос не подставляем — но и молчать больше не будем.
    setState(word, 'error');
    reportProblem('pronounce', e);
  }
}

/** Отдельный элемент для длинных записей: речь собеседника, свои записи. */
let mediaElement: HTMLAudioElement | null = null;

/**
 * Проиграть звук по ссылке, не создавая одноразовый объект.
 *
 * ⚠️ ТА ЖЕ БЕДА, ЧТО И СО СЛОВАМИ, только заметнее: голос ИИ-собеседника
 * синтезируется ПОСЛЕ ожидания сервера, и `new Audio(url).play()` в локальной
 * переменной обрывался сборщиком мусора на середине фразы.
 */
export function playUrl(url: string): void {
  if (!mediaElement) {
    mediaElement = new Audio();
    mediaElement.preload = 'auto';
  }
  mediaElement.src = url;
  void mediaElement.play().catch((e) => reportProblem('playback', e));
}
